import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgClass, NgIf } from '@angular/common';
import { MeasuredValue, PickerUnit, VerticalPickerQuestion } from '../profile-question.model';

type Column = 'whole' | 'fraction' | 'unit';

/** Pixels of vertical drag that move a column by one row. */
const DRAG_STEP = 40;

/** Figma: IVY-HA 235:19769 (desktop) / 235:19681 (mobile). */
@Component({
  selector: 'app-vertical-picker-question',
  standalone: true,
  imports: [NgClass, NgIf],
  templateUrl: './vertical-picker-question.component.html'
})
export class VerticalPickerQuestionComponent implements OnInit {
  @Input({ required: true }) question!: VerticalPickerQuestion;
  @Output() answer = new EventEmitter<MeasuredValue>();

  unitIndex = 0;
  /** Value in the current unit, counted in subunits (e.g. cm for m) so steps stay exact. */
  private scaled = 0;
  private drag: { column: Column; startY: number; moved: number } | null = null;
  private dragged = false;

  get unit(): PickerUnit {
    return this.question.units[this.unitIndex];
  }

  /** Fraction column steps per whole number. */
  private get scale(): number {
    return this.unit.subunits;
  }

  get hasFraction(): boolean {
    return this.unit.subunits > 1;
  }

  /** A single unit has no unit column, like the app's birth year wheel. */
  get hasUnitColumn(): boolean {
    return this.question.units.length > 1;
  }

  /** Whole number, then fraction and unit columns with 1px dividers between them. */
  get gridColumns(): string {
    if (this.hasFraction && this.hasUnitColumn) return 'grid-cols-[98px_1px_67px_1px_1fr]';
    if (this.hasFraction || this.hasUnitColumn) return 'grid-cols-[98px_1px_1fr]';
    return 'grid-cols-1';
  }

  /** A lone whole-number column is centred; otherwise it sits against the divider. */
  get wholeAlign(): string {
    return this.hasFraction || this.hasUnitColumn ? 'right-[10px]' : 'inset-x-0 text-center';
  }

  ngOnInit() {
    const initialIndex = this.question.units.findIndex((u) => u.label === this.question.initialUnit);
    this.unitIndex = Math.max(0, initialIndex);
    this.setValue(this.question.initial);
  }

  get value(): number {
    return this.scaled / this.scale;
  }

  /** Whole-number column: previous, current and next row (null when no value in range has it). */
  wholeRow(offset: number): string | null {
    const whole = Math.floor(this.scaled / this.scale) + offset;
    if (whole < Math.floor(this.unit.min) || whole > Math.floor(this.unit.max)) return null;
    return String(whole).padStart(this.unit.wholeDigits ?? 0, '0');
  }

  /** Fraction column, e.g. ".4" for 60.4 kg, "65" for 1.65 m or "7" for 5 ft 7 in. */
  fractionRow(offset: number): string | null {
    const candidate = this.scaled + offset;
    if (!this.inRange(candidate)) return null;
    // Decimal subunits keep their digits ("05" cm); inches don't
    const digits = Number.isInteger(Math.log10(this.scale)) ? String(this.scale - 1).length : 0;
    return (this.unit.fractionPrefix ?? '') + String(candidate % this.scale).padStart(digits, '0');
  }

  unitRow(offset: number): string | null {
    return this.question.units[this.unitIndex + offset]?.label ?? null;
  }

  step(column: Column, by: number) {
    if (column === 'unit') {
      this.switchUnit(this.unitIndex + by);
      return;
    }
    const delta = column === 'whole' ? by * this.scale : by;
    this.scaled = this.clamp(this.scaled + delta);
  }

  /** Clicking a neighbour row moves to it, unless the click ends a drag. */
  pick(column: Column, by: number) {
    if (this.dragged) {
      this.dragged = false;
      return;
    }
    this.step(column, by);
  }

  onWheel(column: Column, event: WheelEvent) {
    event.preventDefault();
    this.step(column, Math.sign(event.deltaY));
  }

  onPointerDown(column: Column, event: PointerEvent) {
    this.drag = { column, startY: event.clientY, moved: 0 };
    this.dragged = false;
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event: PointerEvent) {
    if (!this.drag) return;
    // Dragging up brings the next (larger) value into the box
    const steps = Math.round((this.drag.startY - event.clientY) / DRAG_STEP);
    if (steps !== this.drag.moved) {
      this.step(this.drag.column, steps - this.drag.moved);
      this.drag.moved = steps;
      this.dragged = true;
    }
  }

  onPointerUp() {
    this.drag = null;
  }

  submit() {
    this.answer.emit({ value: this.value, unit: this.unit.label });
  }

  private switchUnit(index: number) {
    const next = this.question.units[index];
    if (!next) return;
    // Convert through the first unit, then round to the new unit's precision
    const inFirstUnit = this.value * this.unit.factor;
    this.unitIndex = index;
    this.setValue(inFirstUnit / next.factor);
  }

  private setValue(value: number) {
    this.scaled = this.clamp(Math.round(value * this.scale));
  }

  private inRange(scaled: number): boolean {
    return scaled >= Math.round(this.unit.min * this.scale) && scaled <= Math.round(this.unit.max * this.scale);
  }

  private clamp(scaled: number): number {
    const min = Math.round(this.unit.min * this.scale);
    const max = Math.round(this.unit.max * this.scale);
    return Math.min(max, Math.max(min, scaled));
  }
}
